/**
 * @fileoverview Sales Performance API client.
 * @description Calls the backend's Sales Performance routes (Confluence 3.5.11.36-.39): the
 * summary, the ranking and the pending-targets list for a period and team, and the CSV export.
 * The export answers with a raw `text/csv` attachment rather than the success envelope, so it is
 * read as text and handed back with a file name; a refusal (fresh sign-in needed, selection too
 * large) still arrives as the usual error envelope and becomes an ApiException.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { httpClient } from '@/shared/api/http-client'

import type {
  SalesPerformanceExportFile,
  SalesPerformancePending,
  SalesPerformanceQuery,
  SalesPerformanceRanking,
  SalesPerformanceSummary,
} from '@/features/sales/types/sales-performance.types'

const BASE_PATH = '/sales/performance'

/**
 * @description Turns the selection into query parameters, leaving out a team that was not chosen.
 * @param {SalesPerformanceQuery} query - The period and team.
 * @returns {Record<string, string>} The parameters to send.
 */
function toParams(query: SalesPerformanceQuery): Record<string, string> {
  return query.team ? { period: query.period, team: query.team } : { period: query.period }
}

/**
 * @description Fetches the KPIs, monthly series, team comparison and filter options.
 * @param {SalesPerformanceQuery} query - The period and team.
 * @returns {Promise<SalesPerformanceSummary>} The summary.
 */
export async function getSalesPerformanceSummary(
  query: SalesPerformanceQuery,
): Promise<SalesPerformanceSummary> {
  const { data } = await httpClient.get<SalesPerformanceSummary>(`${BASE_PATH}/summary`, {
    params: toParams(query),
  })
  return data
}

/**
 * @description Fetches the individual ranking (capped by the backend, with the total).
 * @param {SalesPerformanceQuery} query - The period and team.
 * @returns {Promise<SalesPerformanceRanking>} The ranking.
 */
export async function getSalesPerformanceRanking(
  query: SalesPerformanceQuery,
): Promise<SalesPerformanceRanking> {
  const { data } = await httpClient.get<SalesPerformanceRanking>(`${BASE_PATH}/ranking`, {
    params: toParams(query),
  })
  return data
}

/**
 * @description Fetches the pending targets (capped by the backend, with the totals).
 * @param {SalesPerformanceQuery} query - The period and team.
 * @returns {Promise<SalesPerformancePending>} The pending list.
 */
export async function getSalesPerformancePending(
  query: SalesPerformanceQuery,
): Promise<SalesPerformancePending> {
  const { data } = await httpClient.get<SalesPerformancePending>(`${BASE_PATH}/pending`, {
    params: toParams(query),
  })
  return data
}

/**
 * @description Reads the file name out of a `Content-Disposition` header.
 * @param {unknown} header - The header's value, if the response carried one.
 * @returns {string | null} The file name, or null when there is none.
 */
function filenameFromDisposition(header: unknown): string | null {
  if (typeof header !== 'string') return null
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header)
  if (!match?.[1]) return null
  try {
    return decodeURIComponent(match[1])
  } catch {
    return match[1]
  }
}

/**
 * @description Exports the selected period and team as one CSV file (Super Admin only, with a
 * fresh MFA sign-in). The file is audited by the backend.
 * @param {SalesPerformanceQuery} query - The period and team.
 * @returns {Promise<SalesPerformanceExportFile>} The CSV text and the file name to save it as.
 */
export async function exportSalesPerformance(
  query: SalesPerformanceQuery,
): Promise<SalesPerformanceExportFile> {
  const response = await httpClient.post<string>(`${BASE_PATH}/export`, toParams(query))
  const filename =
    filenameFromDisposition((response.headers as Record<string, unknown>)['content-disposition']) ??
    `sales-performance-${query.period.toLowerCase()}.csv`
  return { filename, csv: response.data }
}
